import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconChartBar, IconSearch } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import { abcAnalysisAPI } from '../api/client';
import { alertDialog, confirmDialog, toast } from '../components/AppDialog';
import { COLORS, badge, btn, card, input } from '../utils/uiStyles';
import { fmtKstDateTime } from '../utils/kstTime';
import { Page, PageHeader, Button } from '../components/ui';

// 시안 F 색 토큰 — 폴백 hex 는 COLORS 와 같은 값(components/ui 의 V 와 같은 방식).
const V = {
  text: `var(--gw-text, ${COLORS.text})`,
  secondary: `var(--gw-secondary, ${COLORS.textSub})`,
  muted: `var(--gw-muted, ${COLORS.textMuted})`,
  line: `var(--gw-line, ${COLORS.borderSoft})`,
  border: `var(--gw-border, ${COLORS.inputBorder})`,
  surface: `var(--gw-surface, ${COLORS.surface})`,
  bg: `var(--gw-bg, ${COLORS.bg})`,
};
const th = { height: '33px', padding: '5px 9px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: V.muted, whiteSpace: 'nowrap', borderBottom: `1px solid ${V.line}` };
const td = { height: '41px', padding: '7px 9px', fontSize: '13px', color: V.secondary, borderBottom: `1px solid ${V.line}`, whiteSpace: 'nowrap' };
// SKU 표 14열의 클래스(열 순서) — ≤600 에서는 abc-col-m 10열을 숨기고 SKU코드·상품명·월 출고수량·등급만 남긴다.
const ABC_COL_CLASS = [
  'abc-col-m', 'abc-col-m', 'abc-col-m', 'abc-col-sku', 'abc-col-name', 'abc-col-qty', 'abc-col-m',
  'abc-col-m', 'abc-col-grade', 'abc-col-m', 'abc-col-m', 'abc-col-m', 'abc-col-m', 'abc-col-m',
];
// 🔴 인라인 minWidth·nowrap 을 ≤600 에서만 이기도록 !important · 등급 묶음(3칸)은 1열 + 칸마다 아래 선(마지막 칸 없음).
const NARROW_CSS = `@media (max-width:600px){.abc-sku-table{min-width:0!important;table-layout:fixed}.abc-sku-table .abc-col-m{display:none}.abc-sku-table th,.abc-sku-table td{white-space:normal!important;word-break:break-word}.abc-sku-table th{word-break:keep-all}.abc-sku-table .abc-col-sku{width:34%}.abc-sku-table .abc-col-name{width:32%}.abc-sku-table .abc-col-qty{width:20%}.abc-sku-table .abc-col-grade{width:14%}.abc-grade-metrics{grid-template-columns:minmax(0,1fr)!important}.abc-grade-metrics>.gw-metric{border-right:0!important;border-bottom:1px solid ${V.line}!important}.abc-grade-metrics>.gw-metric:last-child{border-bottom:0!important}}`;
const metricLabel = { fontSize: '12px', color: V.secondary };
// 조건 줄 select·검색 상자 높이 31 = Button md 와 같은 줄 높이.
const toolSelect = { height: '31px', padding: '0 8px', fontSize: '12px' };
const searchBox = {
  display: 'flex', alignItems: 'center', gap: '7px', height: '31px', padding: '0 9px', boxSizing: 'border-box',
  border: `1px solid ${V.border}`, borderRadius: '5px', background: V.surface,
};
const searchInput = { border: 0, background: 'transparent', flex: 1, minWidth: 0, width: '100%', fontSize: '12px', padding: 0, color: V.text };
const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
const pct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`);
// 🔴 서버는 UTC naive 로 준다 — 그대로 자르면 엑셀(KST)과 9시간 차가 난다(2026-10-03).
const dateTime = (v) => (v ? (fmtKstDateTime(v) || '—') : '—');
const statusName = { done: '완료', running: '분석 중', blocked: '미완료', failed: '실패', superseded: '이전 완료본' };
const gradeTone = { A: 'success', B: 'warn', C: 'danger' };
const requestError = (e) => e.response?.data?.detail || e.message || '조회에 실패했습니다.';
const isCanceled = (e, signal) => signal.aborted || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';
const hasText = (value, needle) => String(value || '').toLowerCase().includes(needle);
// FastAPI 검증 오류(422)는 detail 이 배열이다 → 문구만 이어 붙인다.
const errorDetail = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg).filter(Boolean).join(' / ') || fallback;
  return detail || e?.message || fallback;
};

// 재고(과다·장기·악성) 상태 — 서버 abc_stock.STATUS_ORDER 와 같은 코드.
const stockStatusName = { dead: '악성', long: '장기', over: '과다', caution: '주의', normal: '정상', history_short: '이력 부족' };
const stockStatusTone = { dead: 'danger', long: 'danger', over: 'warn', caution: 'warn', normal: 'success', history_short: 'default' };
const STOCK_STATUS_OPTIONS = [['', '전체'], ['normal', '정상'], ['caution', '주의'], ['over', '과다'], ['long', '장기'], ['dead', '악성'], ['history_short', '이력 부족']];
const fixed = (v, digits) => (v === null || v === undefined ? '—' : Number(v).toFixed(digits));
const outBasisText = (rules) => `월 출고${rules?.include_kit_out ? ' + 키팅 출고' : ''}${rules?.deduct_return ? ' − 반품' : ''}`;
// 빈 칸·소수는 null 로 보낸다 → 서버가 422 로 돌려준다(화면에서 몰래 고치지 않는다).
const toInt = (v) => {
  const text = String(v ?? '').trim();
  if (!text) return null;
  const n = Number(text);
  return Number.isInteger(n) ? n : null;
};
const RULE_NUMBER_FIELDS = [
  ['A', 'A 과다 기준일수'], ['B', 'B 과다 기준일수'], ['C', 'C 과다 기준일수'],
  ['caution_pct', '주의 비율(%)'], ['long_months', '장기 기준 개월'], ['dead_months', '악성 기준 개월'],
];

// 월간 재고 알림 받는 사람 — [받는 사람 관리]를 눌렀을 때만 그려지고 그때 처음 API 를 부른다.
function NotifyTargetsPanel({ onClose }) {
  const [targets, setTargets] = useState([]);
  const [channels, setChannels] = useState({ enabled: false, items: [] });
  const [channelId, setChannelId] = useState('');
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const aliveRef = useRef(true);

  const refresh = useCallback(async () => {
    const response = await abcAnalysisAPI.notifyTargets();
    if (aliveRef.current) setTargets(response.data?.items || []);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    (async () => {
      setBusy(true);
      try {
        await refresh();
        try {
          const response = await abcAnalysisAPI.notifyChannels();
          if (aliveRef.current) setChannels(response.data || { enabled: false, items: [] });
        } catch (e) {
          if (aliveRef.current) setChannels({ enabled: false, items: [], error: errorDetail(e, '메신저 채널 목록을 불러오지 못했습니다.') });
        }
      } catch (e) {
        if (aliveRef.current) await alertDialog(`받는 사람 목록을 불러오지 못했습니다: ${errorDetail(e, '알 수 없는 오류')}`);
      } finally {
        if (aliveRef.current) {
          setBusy(false);
          setLoaded(true);
        }
      }
    })();
    return () => { aliveRef.current = false; };
  }, [refresh]);

  const run = async (task, failText) => {
    setBusy(true);
    try {
      await task();
    } catch (e) {
      await alertDialog(`${failText}: ${errorDetail(e, '알 수 없는 오류')}`);
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  const search = () => {
    const term = query.trim();
    if (!term) return;
    run(async () => {
      const response = await abcAnalysisAPI.notifyUsers(term);
      if (aliveRef.current) setUsers(response.data?.items || []);
    }, '사람 검색에 실패했습니다');
  };

  const add = (body) => run(async () => {
    await abcAnalysisAPI.addNotifyTarget(body);
    toast('받는 사람을 추가했습니다.');
    await refresh();
  }, '받는 사람 추가에 실패했습니다');

  const targetLabel = (t) => (t.kind === 'mm_user'
    ? `사람 · ${t.user_name || t.user_id}${t.email_hint ? ` (${t.email_hint})` : ''}`
    : `채널 · ${t.channel_label || t.channel_id || '—'}`);

  const remove = async (target) => {
    if (!await confirmDialog(`${targetLabel(target)} 을(를) 받는 사람에서 뺄까요?`, { title: '받는 사람 삭제' })) return;
    run(async () => {
      await abcAnalysisAPI.deleteNotifyTarget(target.id);
      await refresh();
    }, '받는 사람 삭제에 실패했습니다');
  };

  return (
    <div data-testid="abc-stock-notify" style={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${COLORS.borderSoft}` }}>
      <div style={{ color: COLORS.text, fontWeight: 600 }}>월간 재고 알림 받는 사람</div>
      <div>월간 자동 분석이 끝나면 한 달에 한 번 메신저로 과다·장기·악성 현황을 보냅니다. 받는 사람이 0명이면 보내지 않습니다.</div>
      {loaded && !targets.length && <div data-testid="abc-notify-empty" style={{ color: COLORS.warn }}>등록된 받는 사람이 없어 알림을 보내지 않습니다.</div>}
      {targets.map((t) => <div key={t.id} data-testid="abc-notify-target" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <span>{targetLabel(t)}</span>
        <button type="button" aria-label={`${targetLabel(t)} 삭제`} disabled={busy} onClick={() => remove(t)} style={btn('danger', 'sm')}>삭제</button>
      </div>)}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
        <input type="search" aria-label="받는 사람 검색" placeholder="이름·이메일" value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
          style={{ ...input, width: '160px' }} />
        <button type="button" disabled={busy || !query.trim()} onClick={search} style={btn('secondary', 'sm')}>사람 찾기</button>
        {users.map((u) => <button key={u.id} type="button" aria-label={`${u.name} 추가`} disabled={busy}
          onClick={() => add({ kind: 'mm_user', user_id: u.id })} style={btn('accent', 'sm')}>+ {u.name}{u.email_hint ? ` (${u.email_hint})` : ''}</button>)}
      </div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
        <select aria-label="알림 채널" value={channelId} disabled={!channels.enabled || busy} onChange={(e) => setChannelId(e.target.value)} style={{ ...input, width: '240px' }}>
          <option value="">{channels.enabled ? '채널 선택' : (channels.error || '메신저 봇이 꺼져 있습니다')}</option>
          {(channels.items || []).map((c) => <option key={c.id} value={c.id}>{c.label || `${c.team} / ${c.name}`}</option>)}
        </select>
        <button type="button" disabled={busy || !channelId} onClick={() => add({ kind: 'mm_channel', channel_id: channelId })} style={btn('secondary', 'sm')}>채널 추가</button>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onClose} style={btn('secondary', 'sm')}>받는 사람 관리 닫기</button>
      </div>
      {/* 사람은 그 사람의 센터 권한 범위만 받지만, 채널은 구성원 권한과 무관하게 전사 범위를 받는다. */}
      <div data-testid="abc-notify-channel-note" style={{ color: COLORS.warn, marginTop: '4px' }}>
        채널에는 활성 센터 전체의 화주·SKU 현황이 갑니다(채널 구성원의 센터 권한과 무관). 받아도 되는 채널만 고르세요.
      </div>
    </div>
  );
}

// 재고 기준값 카드 — ABC 메뉴 권한자 모두에게 현재 값·최종 변경자를 보여 주고, can_edit(운영·시스템 관리자)만 고친다.
function StockRulesCard({ stockRules, onSaved }) {
  const rules = stockRules.rules || {};
  const over = rules.over_days || {};
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);

  const startEdit = () => setDraft({
    include_kit_out: Boolean(rules.include_kit_out),
    deduct_return: Boolean(rules.deduct_return),
    A: String(over.A ?? ''), B: String(over.B ?? ''), C: String(over.C ?? ''),
    caution_pct: String(rules.caution_pct ?? ''),
    long_months: String(rules.long_months ?? ''),
    dead_months: String(rules.dead_months ?? ''),
  });

  const save = async () => {
    setSaving(true);
    try {
      await abcAnalysisAPI.saveStockRules({
        include_kit_out: draft.include_kit_out,
        deduct_return: draft.deduct_return,
        over_days: { A: toInt(draft.A), B: toInt(draft.B), C: toInt(draft.C) },
        caution_pct: toInt(draft.caution_pct),
        long_months: toInt(draft.long_months),
        dead_months: toInt(draft.dead_months),
      });
      setDraft(null);
      toast('재고 기준값을 저장했습니다. 다음 조회부터 새 기준으로 판정합니다.');
      await onSaved();
    } catch (e) {
      await alertDialog(`기준값 저장에 실패했습니다: ${errorDetail(e, '알 수 없는 오류')}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="abc-stock-rules" aria-label="재고 기준값" style={{ ...card, padding: '11px 14px', marginBottom: '12px', fontSize: '12px', color: COLORS.textSub, lineHeight: 1.8 }}>
      <strong style={{ color: COLORS.text }}>재고 기준값</strong>
      <span> · 과다: 재고일수 A {over.A}일 · B {over.B}일 · C {over.C}일 이상</span>
      <span> · 주의: 과다 기준일수의 {rules.caution_pct}% 이상</span>
      <span> · 연속 무출고 장기 {rules.long_months}개월 · 악성 {rules.dead_months}개월</span>
      <span> · 출고 정의: {outBasisText(rules)}</span>
      <span data-testid="abc-stock-rules-updated"> · 최종 변경: {stockRules.updated_by_name || '—'} / {dateTime(stockRules.updated_at)}</span>
      {stockRules.can_edit && !draft && <span style={{ marginLeft: '8px', display: 'inline-flex', gap: '6px' }}>
        <button type="button" onClick={startEdit} style={btn('secondary', 'sm')}>기준값 수정</button>
        <button type="button" onClick={() => setNotifyOpen((v) => !v)} style={btn('secondary', 'sm')}>받는 사람 관리</button>
      </span>}
      {stockRules.can_edit && draft && <div data-testid="abc-stock-rules-form" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
        <label><input type="checkbox" aria-label="키팅 출고 포함" checked={draft.include_kit_out}
          onChange={(e) => setDraft((d) => ({ ...d, include_kit_out: e.target.checked }))} /> 키팅 출고 포함</label>
        <label><input type="checkbox" aria-label="반품 차감" checked={draft.deduct_return}
          onChange={(e) => setDraft((d) => ({ ...d, deduct_return: e.target.checked }))} /> 반품 차감</label>
        {RULE_NUMBER_FIELDS.map(([key, label]) => <input key={key} type="number" aria-label={label} title={label} value={draft[key]}
          onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} style={{ ...input, width: '88px' }} />)}
        <button type="button" onClick={save} disabled={saving} style={btn('primary', 'sm')}>저장</button>
        <button type="button" onClick={() => setDraft(null)} disabled={saving} style={btn('secondary', 'sm')}>취소</button>
        <span style={{ color: COLORS.textMuted }}>일수 1~3650 · 주의 1~99% · 장기 1~11개월 · 악성 2~12개월 · 장기 &lt; 악성</span>
      </div>}
      {stockRules.can_edit && notifyOpen && <NotifyTargetsPanel onClose={() => setNotifyOpen(false)} />}
    </section>
  );
}

// 1과 마지막은 항상, 현재 쪽 앞뒤 2쪽만 버튼으로 그린다(93쪽이면 버튼 93개가 되지 않게).
const pageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const picked = new Set([1, total]);
  for (let n = current - 2; n <= current + 2; n += 1) if (n >= 1 && n <= total) picked.add(n);
  const sorted = [...picked].sort((a, b) => a - b);
  return sorted.flatMap((n, i) => (i > 0 && n - sorted[i - 1] > 1 ? [`gap-${n}`, n] : [n]));
};

// jsdom 16 에는 Blob.text() 가 없다 → FileReader 갈래로 읽는다(관리자 문서 다운로드와 동일).
const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

export default function AbcAnalysisPage() {
  const [opts, setOpts] = useState({ months: [], centers: [], shippers: [], schedule: {} });
  const [ym, setYm] = useState('');
  const [center, setCenter] = useState('');
  const [pwn, setPwn] = useState('');
  // 기본값(조회 범위 전체 · 전체 상태)이면 list·export 파라미터에 키를 넣지 않는다(기존 6키 계약).
  const [gradeBasis, setGradeBasis] = useState('scope');
  const [stockStatus, setStockStatus] = useState('');
  const [sort, setSort] = useState('qty_desc');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showZero, setShowZero] = useState(false);
  const [centerQuery, setCenterQuery] = useState('');
  const [pwnQuery, setPwnQuery] = useState('');
  const [jumpPage, setJumpPage] = useState('');
  const [downloading, setDownloading] = useState(false);
  const mountedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const requestControllerRef = useRef(null);
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const privileged = user.role === 'admin' || user.role === 'ops';

  const beginRequest = useCallback(() => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    const sequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = sequence;
    requestControllerRef.current = controller;
    return { controller, sequence };
  }, []);

  const isCurrentRequest = useCallback(({ controller, sequence }) => (
    mountedRef.current
    && requestSequenceRef.current === sequence
    && requestControllerRef.current === controller
  ), []);

  const invalidateRequest = useCallback(() => {
    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
    requestSequenceRef.current += 1;
    if (mountedRef.current) setBusy(false);
  }, []);

  const fetchOptions = useCallback(async (selectedYm = '') => {
    const request = beginRequest();
    setBusy(true);
    setError('');
    try {
      const response = await abcAnalysisAPI.options(selectedYm, request.controller.signal);
      if (!isCurrentRequest(request)) return null;
      const options = response.data || { months: [], centers: [], shippers: [], schedule: {} };
      setOpts(options);
      return options;
    } catch (e) {
      if (!isCurrentRequest(request) || isCanceled(e, request.controller.signal)) return null;
      setError(requestError(e));
      return null;
    } finally {
      if (isCurrentRequest(request)) {
        requestControllerRef.current = null;
        setBusy(false);
      }
    }
  }, [beginRequest, isCurrentRequest]);

  const load = useCallback(async ({
    targetPage = page,
    targetSort = sort,
    targetYm = ym,
    targetCenter = center,
    targetPwn = pwn,
    targetGradeBasis = gradeBasis,
    targetStockStatus = stockStatus,
  } = {}) => {
    if (!targetYm) return;
    const request = beginRequest();
    setBusy(true);
    setError('');
    try {
      const params = { ym: targetYm, center: targetCenter, pwn: targetPwn, page: targetPage, size: 50, sort: targetSort };
      if (targetGradeBasis && targetGradeBasis !== 'scope') params.grade_basis = targetGradeBasis;
      if (targetStockStatus) params.stock_status = targetStockStatus;
      const response = await abcAnalysisAPI.list(params, request.controller.signal);
      if (!isCurrentRequest(request)) return;
      setData(response.data);
      setPage(targetPage);
    } catch (e) {
      if (!isCurrentRequest(request) || isCanceled(e, request.controller.signal)) return;
      setError(requestError(e));
      setData(null);
    } finally {
      if (isCurrentRequest(request)) {
        requestControllerRef.current = null;
        setBusy(false);
      }
    }
  }, [beginRequest, center, gradeBasis, isCurrentRequest, page, pwn, sort, stockStatus, ym]);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      const options = await fetchOptions();
      if (!options || !mountedRef.current) return;
      // 완료 월이 없을 때만 최근 blocked/failed 월을 유지해 실제 사유를 보여 준다.
      const initialYm = options.default_ym || options.fallback_ym || options.months?.[0]?.ym || '';
      setYm(initialYm);
      if (initialYm) {
        await load({
          targetYm: initialYm, targetCenter: '', targetPwn: '', targetPage: 1, targetSort: 'qty_desc',
          targetGradeBasis: 'scope', targetStockStatus: '',
        });
      }
    })();
    return () => {
      mountedRef.current = false;
      requestControllerRef.current?.abort();
      requestControllerRef.current = null;
      requestSequenceRef.current += 1;
    };
  }, [fetchOptions]);

  const changeYm = async (value) => {
    setYm(value);
    setPwn('');
    setPage(1);
    setData(null);
    await fetchOptions(value);
  };

  const changeCenter = (value) => {
    invalidateRequest();
    setCenter(value);
    setPage(1);
    setData(null);
    setError('');
  };

  const changePwn = (value) => {
    invalidateRequest();
    setPwn(value);
    setPage(1);
    setData(null);
    setError('');
  };

  // 등급 기준·재고 상태도 센터·화주처럼 결과를 비우고 [조회]를 기다린다.
  const changeGradeBasis = (value) => {
    invalidateRequest();
    setGradeBasis(value);
    setPage(1);
    setData(null);
    setError('');
  };

  const changeStockStatus = (value) => {
    invalidateRequest();
    setStockStatus(value);
    setPage(1);
    setData(null);
    setError('');
  };

  const changeSort = (value) => {
    setSort(value);
    load({ targetPage: 1, targetSort: value });
  };

  // 기준값 저장 뒤 — 새 기준값(options.stock_rules)과 새 판정(list)을 다시 받는다.
  const reloadAfterRules = async () => {
    await fetchOptions(ym);
    await load({ targetPage: 1 });
  };

  const rerun = async () => {
    if (!await confirmDialog(`${ym} 분석을 새 실행으로 다시 시작할까요?`, { title: 'ABC 재분석' })) return;
    try {
      await abcAnalysisAPI.rerun({ ym });
      toast('재분석 요청을 접수했습니다. 기존 완료 결과는 새 실행이 끝날 때까지 유지됩니다.');
      await fetchOptions(ym);
    } catch (e) {
      await alertDialog(e.response?.data?.detail || '재분석 요청에 실패했습니다.');
    }
  };

  const run = data?.run;
  const attempt = data?.latest_attempt;
  const summary = data?.summary;
  // 재고 지표는 list 응답 최상위 stock 으로 온다. 키가 없으면(옛 서버) 카드를 그리지 않고 표는 '—'.
  const stock = data?.stock;
  const stockApplicable = Boolean(stock?.applicable);
  const stockRules = opts.stock_rules;
  // 🔴 rule_version 이 없는 옛 응답은 1(옛 기준)로 읽는다 — `undefined < 2` 는 false 라
  //    «< 2 면 옛 문구»로 짜면 값이 없는 응답이 새 문구로 간다.
  const newRule = Number(run?.rule_version ?? 1) >= 2;
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / 50));

  // 검색은 화면 안에서만 거른다(서버 호출 없음). 지금 고른 값은 걸러져도 옵션에 남긴다.
  const centerOptions = useMemo(() => {
    const all = opts.centers || [];
    const needle = centerQuery.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((c) => c.center_key === center || hasText(c.display_name, needle) || hasText(c.center_key, needle));
  }, [opts.centers, centerQuery, center]);

  const shipperOptions = useMemo(() => {
    const all = opts.shippers || [];
    const needle = pwnQuery.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((s) => s.pwn_cd === pwn || hasText(s.pwn_nm, needle) || hasText(s.pwn_cd, needle));
  }, [opts.shippers, pwnQuery, pwn]);

  const goToPage = () => {
    const parsed = Number.parseInt(String(jumpPage).trim(), 10);
    if (!Number.isFinite(parsed)) return;
    setJumpPage('');
    load({ targetPage: Math.min(totalPages, Math.max(1, parsed)) });
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const params = { ym, center: center || undefined, pwn: pwn || undefined, sort };
      if (gradeBasis !== 'scope') params.grade_basis = gradeBasis;
      if (stockStatus) params.stock_status = stockStatus;
      const response = await abcAnalysisAPI.exportExcel(params);
      const disposition = response.headers?.['content-disposition'] || '';
      const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
      let filename = `ABC분석_${ym}.xlsx`;
      if (encoded) {
        try { filename = decodeURIComponent(encoded.trim()); } catch { /* 기본 이름을 쓴다 */ }
      }
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.response?.data?.detail;
      const errorData = e.response?.data;
      if (!detail && errorData instanceof Blob) {
        try {
          const text = await readBlobText(errorData);
          try { detail = JSON.parse(text)?.detail; } catch { detail = text; }
        } catch { /* 기본 문구를 쓴다 */ }
      }
      await alertDialog('엑셀 내려받기에 실패했습니다: ' + (detail || e.message || '알 수 없는 오류'));
    } finally {
      if (mountedRef.current) setDownloading(false);
    }
  };

  return (
    <AppShell app="abc-analysis">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: V.bg }}>
        <Page>
          <style>{NARROW_CSS}</style>
          {/* «불러오는 중…» 은 늘 그려 자리를 잡아 둔다(≤600 에서 조회마다 아래가 흔들리지 않게) — 쉬는 동안엔 role 이 없다. */}
          <PageHeader
            icon={IconChartBar}
            tone="green"
            title="ABC분석"
            subtitle="월 출고수량 누적 비중으로 SKU를 A(80% 이전)·B(95% 이전)·C 등급으로 나눕니다. 동일 수량은 같은 등급입니다."
            actions={<span role={busy ? 'status' : undefined} style={{ color: V.muted, fontSize: '12px', whiteSpace: 'nowrap', visibility: busy ? 'visible' : 'hidden' }}>불러오는 중…</span>}
          />

          <section aria-label="ABC 분석 조건" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
            <label style={{ fontSize: '12px' }}>연월 <select aria-label="연월" value={ym} onChange={(e) => changeYm(e.target.value)} style={{ ...input, width: 'auto', minWidth: '130px', marginLeft: '4px', ...toolSelect }}>
              {(opts.months || []).map((m) => <option key={m.ym} value={m.ym}>{m.ym} · {statusName[m.status] || m.status}</option>)}
            </select></label>
            {/* 🔴 검색 입력은 select 의 <label> 바깥에 둔다 — 안에 넣으면 select 의 접근 이름이 바뀐다.
                공용 SearchInput 은 type="text" 라 searchbox 역할이 바뀐다 → 같은 모양을 여기서 그린다. */}
            <div style={{ ...searchBox, width: '150px' }}>
              <IconSearch size={14} stroke={1.65} aria-hidden="true" style={{ color: V.muted, flexShrink: 0 }} />
              <input type="search" aria-label="센터 이름 검색" placeholder="센터 이름" value={centerQuery}
                onChange={(e) => setCenterQuery(e.target.value)} style={searchInput} />
            </div>
            {centerQuery.trim() && <span style={{ fontSize: '11px', color: COLORS.textMuted }}>{centerOptions.length}건</span>}
            <label style={{ fontSize: '12px' }}>센터 <select aria-label="센터" value={center} onChange={(e) => changeCenter(e.target.value)} style={{ ...input, width: '180px', marginLeft: '4px', ...toolSelect }}>
              <option value="">권한 내 전체 센터</option>
              {centerOptions.map((c) => <option key={c.center_key} value={c.center_key}>{c.display_name}</option>)}
            </select></label>
            <div style={{ ...searchBox, width: '160px' }}>
              <IconSearch size={14} stroke={1.65} aria-hidden="true" style={{ color: V.muted, flexShrink: 0 }} />
              <input type="search" aria-label="화주 이름 검색" placeholder="화주명·코드" value={pwnQuery}
                onChange={(e) => setPwnQuery(e.target.value)} style={searchInput} />
            </div>
            {pwnQuery.trim() && <span style={{ fontSize: '11px', color: COLORS.textMuted }}>{shipperOptions.length}건</span>}
            <label style={{ fontSize: '12px' }}>화주 <select aria-label="화주" value={pwn} onChange={(e) => changePwn(e.target.value)} style={{ ...input, width: '210px', marginLeft: '4px', ...toolSelect }}>
              <option value="">전체 화주</option>
              {shipperOptions.map((s) => <option key={s.pwn_cd} value={s.pwn_cd}>{s.pwn_nm} ({s.pwn_cd})</option>)}
            </select></label>
            <label style={{ fontSize: '12px' }}>등급 기준 <select aria-label="등급 기준" value={gradeBasis} onChange={(e) => changeGradeBasis(e.target.value)} style={{ ...input, width: '140px', marginLeft: '4px', ...toolSelect }}>
              <option value="scope">조회 범위 전체</option>
              <option value="shipper">화주별</option>
            </select></label>
            <label style={{ fontSize: '12px' }}>재고 상태 <select aria-label="재고 상태" value={stockStatus} onChange={(e) => changeStockStatus(e.target.value)} style={{ ...input, width: '110px', marginLeft: '4px', ...toolSelect }}>
              {STOCK_STATUS_OPTIONS.map(([value, label]) => <option key={value || 'all'} value={value}>{label}</option>)}
            </select></label>
            <Button variant="primary" onClick={() => load({ targetPage: 1 })} disabled={busy || !ym}>조회</Button>
            {privileged && <Button variant="secondary" onClick={rerun} disabled={busy || !ym}>↻ 재분석</Button>}
          </section>

          {error && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>{error}</div>}

          {(run || opts.schedule) && <section data-testid="abc-status" style={{ ...card, padding: '11px 14px', marginBottom: '12px', fontSize: '12px', color: COLORS.textSub, lineHeight: 1.8 }}>
            <strong style={{ color: COLORS.text }}>분석 상태: {statusName[run?.status] || run?.status || '완료된 분석 없음'}</strong>
            {run?.blocked_reason && <span style={{ color: COLORS.warn }}> · 사유: {run.blocked_reason}</span>}
            <span> · 최종 분석 시각: {dateTime(run?.finished_at)}</span>
            <span> · 자동 실행: {opts.schedule?.cron_label || '매월 1~8일 08:10 KST'}</span>
            <span> · 최근 자동 실행: {opts.schedule?.last_auto_run_ym || '—'} / {dateTime(opts.schedule?.last_auto_run_at)} / {statusName[opts.schedule?.last_auto_run_status] || opts.schedule?.last_auto_run_status || '—'}</span>
            <span> · 원천 적재 기준: {run?.source_to || '—'} ({run?.source_last_run || '—'})</span>
            <span> · 수량 규칙: {run?.qty_rule || 'sum(out_qty)'}</span>
            <span> · 반품 차감: 미적용(정책 미확정)</span>
            {attempt && <div style={{ color: attempt.status === 'failed' ? COLORS.dangerText : COLORS.warn }}>
              기존 완료 결과 표시 중 · 최근 재실행 {statusName[attempt.status] || attempt.status}: {attempt.blocked_reason || dateTime(attempt.finished_at)}
            </div>}
            {Number.isInteger(data?.unmapped_depot_count) && <div data-testid="unmapped-depot-count" style={{ color: data.unmapped_depot_count > 0 ? COLORS.warn : COLORS.textMuted }}>대장 미등록 창고 {data.unmapped_depot_count}개</div>}
          </section>}

          {stockRules?.rules && <StockRulesCard stockRules={stockRules} onSaved={reloadAfterRules} />}

          {run && run.status !== 'done' && !summary && <div data-testid="abc-blocked" style={{ ...card, background: COLORS.warnSoft, color: COLORS.warn, marginBottom: '12px' }}>
            분석 미완료{run.blocked_reason ? ` · ${run.blocked_reason}` : ''}. 출고 0으로 간주하지 않습니다.
          </div>}
          {summary?.empty && <div data-testid="abc-empty" style={{ ...card, background: COLORS.infoSoft, color: COLORS.info, marginBottom: '12px' }}>분석 대상 출고 없음</div>}

          {(data?.centers_missing || []).length > 0 && <section data-testid="centers-missing" style={{ ...card, background: COLORS.warnSoft, marginBottom: '12px' }}>
            <strong style={{ color: COLORS.warn }}>누락·확인 필요 센터</strong>
            {(data.centers_missing || []).map((c) => <div key={c.center_key} style={{ fontSize: '12px', marginTop: '5px' }}>{c.center_name}: — · {c.reason}</div>)}
          </section>}

          {summary && <>
            <div data-testid="abc-target-rule" style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '10px', lineHeight: 1.7 }}>
              {newRule
                ? `산정 대상: ${run?.ym || ym} 말일 재고 > 0 또는 그 달 수불이 있는 상품 · 출고 0 은 C`
                : '산정 대상: 옛 기준(출고 > 0 만) — ↻ 재분석하면 새 기준으로 다시 계산됩니다'}
            </div>
            <section aria-label="ABC 분석 요약" style={{ marginBottom: '16px' }}>
              {/* .gw-metrics 는 4열 고정 — 7칸을 한 묶음에 넣으면 선이 어긋나서 4칸 + 등급 3칸 두 묶음으로 나눈다. */}
              <div className="gw-metrics" style={{ marginBottom: '10px' }}>
                {[
                  ['대상 연월', run?.ym || ym, 'month'],
                  ['등급 산정 범위', data?.scope?.label || '—', 'scope'],
                  ['총 출고수량', fmt(summary.total_out_qty), 'total-qty'],
                  ['분석 SKU 수', fmt(summary.sku_count), 'sku-count'],
                ].map(([label, value, key]) => <div key={key} className="gw-metric" data-testid={`summary-${key}`}>
                  <div style={metricLabel}>{label}</div>
                  <strong className="gw-metric-value" style={key === 'scope'
                    ? { display: 'block', fontSize: '14px', fontWeight: 600, letterSpacing: 0, lineHeight: 1.4, whiteSpace: 'normal', wordBreak: 'keep-all' }
                    : { display: 'block' }}>{value}</strong>
                  {key === 'sku-count' && <div style={{ fontSize: '10px', color: V.muted }}>센터 × 화주 × SKU 조합 수</div>}
                </div>)}
              </div>
              <div className="gw-metrics abc-grade-metrics" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', marginBottom: 0 }}>
                {['A', 'B', 'C'].map((grade) => {
                  const g = summary.grades?.[grade];
                  // 🔴 뒤 span 은 인라인 — 칸 글자가 «N개 · 수량 · 비중» 한 줄로 이어져야 한다(e2e 단언).
                  return (
                    <div key={grade} className="gw-metric" data-testid={`summary-grade-${grade}`}>
                      <div style={metricLabel}>{`${grade} 등급`}</div>
                      <strong className="gw-metric-value" style={{ display: 'block' }}>{fmt(g?.sku_count)}개<span style={{ fontSize: '12px', fontWeight: 400, letterSpacing: 0, color: V.muted }}> · {fmt(g?.out_qty)} · {pct(g?.share)}</span></strong>
                    </div>
                  );
                })}
              </div>
            </section>

            {stock && stockApplicable && <section aria-label="재고 상태 요약" data-testid="abc-stock-summary" className="gw-metrics" style={{ marginBottom: '16px' }}>
              {[['over', '과다 재고'], ['long', '장기 재고'], ['dead', '악성 재고']].map(([code, label]) => <div key={code} className="gw-metric" data-testid={`summary-stock-${code}`}>
                <div style={metricLabel}>{label}</div>
                <strong className="gw-metric-value" style={{ display: 'block', color: code === 'over' ? COLORS.warn : COLORS.dangerText }}>{fmt(stock.counts?.[code])}건</strong>
              </div>)}
              <div className="gw-metric" data-testid="summary-stock-caution" style={{ fontSize: '12px', color: V.secondary, lineHeight: 1.7 }}>
                <div>주의 {fmt(stock.counts?.caution)}건 · 이력 부족 {fmt(stock.counts?.history_short)}건 · 정상 {fmt(stock.counts?.normal)}건</div>
                <div style={{ fontSize: '10px', color: COLORS.textMuted }}>등급 기준: {stock.grade_basis === 'shipper' ? '화주별' : '조회 범위 전체'} · 재고 0 은 판정하지 않음</div>
              </div>
            </section>}
            {stock && !stockApplicable && <div data-testid="abc-stock-unavailable" style={{ ...card, background: COLORS.infoSoft, color: COLORS.info, marginBottom: '12px', fontSize: '12px' }}>
              재고 지표: 옛 규칙 실행이라 월말 재고가 없어 계산하지 않습니다(↻ 재분석하면 계산됩니다).
            </div>}

            {data?.issues?.length > 0 && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>수량 데이터 확인 대상 {data.issues.length}건은 ABC 등급에서 제외했습니다.</div>}

            <section>
              <div style={{ padding: '0 0 10px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <strong style={{ fontSize: '14px' }}>SKU 상세</strong>
                <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>총 {fmt(data?.total)}건</span>
                <Button variant="secondary" onClick={() => setShowZero((v) => !v)}>{newRule ? '산정 대상 아님(재고·수불 없음)' : '출고 없음(ABC 제외)'} {fmt(data?.zero_count)}건 {showZero ? '닫기' : '보기'}</Button>
                <span style={{ flex: 1 }} />
                <Button variant="secondary" onClick={downloadExcel} disabled={!summary || busy || downloading}>엑셀 다운로드</Button>
                <select aria-label="정렬" value={sort} onChange={(e) => changeSort(e.target.value)} style={{ ...input, width: '170px', ...toolSelect }}>
                  <option value="qty_desc">출고수량 많은 순</option><option value="qty_asc">출고수량 적은 순</option>
                  <option value="sku_asc">SKU코드 순</option><option value="center_asc">센터 순</option><option value="grade_asc">등급 순</option>
                  <option value="stock_days_desc">재고일수 많은 순</option><option value="idle_desc">연속 무출고 긴 순</option>
                </select>
              </div>
              {showZero && <div data-testid="zero-shipment-note" style={{ padding: '8px 12px', background: COLORS.infoSoft, color: COLORS.info, fontSize: '12px', borderRadius: '5px', marginBottom: '8px' }}>{newRule
                ? `산정 대상 아님 ${fmt(data?.zero_count)}건은 말일 재고도 그 달 수불도 없어 등급과 분모에서 제외됩니다.`
                : `출고 없음 ${fmt(data?.zero_count)}건은 0으로 보관되지만 ABC 분모와 등급에서 제외됩니다.`}</div>}
              <div style={{ overflowX: 'auto' }}>
                {/* 넓은 화면 최소 폭 1300 — .gw-page 본문(최대 1332)에 들어간다 · ≤600 은 NARROW_CSS 가 열을 숨긴다. */}
                <table className="abc-sku-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1300px' }}>
                  <thead><tr>{['순위', '센터', '화주', 'SKU코드', '상품명', '월 출고수량', '출고 비중', '누적 비중', '등급', '월말 재고', '재고일수', '회전율', '연속 무출고', '재고 상태'].map((h, i) => <th key={h} className={ABC_COL_CLASS[i]} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(data?.items || []).map((it) => <tr key={`${it.center_key}:${it.pwn_cd}:${it.prod_cd}`} className="gw-tr" data-testid="abc-item" data-sku-key={`${it.center_key}:${it.pwn_cd}:${it.prod_cd}`}>
                      <td className={ABC_COL_CLASS[0]} style={{ ...td, color: V.text }}>{it.rank}</td><td className={ABC_COL_CLASS[1]} style={td}>{it.center_name}</td><td className={ABC_COL_CLASS[2]} style={td}>{it.pwn_nm || it.pwn_cd}</td>
                      <td className={ABC_COL_CLASS[3]} style={td}>{it.prod_sku || it.prod_cd}</td><td className={ABC_COL_CLASS[4]} style={{ ...td, whiteSpace: 'normal', minWidth: '180px' }}>{it.product_name || '—'}</td>
                      <td className={ABC_COL_CLASS[5]} data-testid="abc-out-qty" style={{ ...td, textAlign: 'right' }}>{fmt(it.out_qty)}</td><td className={ABC_COL_CLASS[6]} style={{ ...td, textAlign: 'right' }}>{pct(it.share)}</td><td className={ABC_COL_CLASS[7]} style={{ ...td, textAlign: 'right' }}>{pct(it.cum_share)}</td>
                      <td className={ABC_COL_CLASS[8]} style={td}><span data-testid="abc-grade" style={badge(gradeTone[it.grade])}>{it.grade}</span></td>
                      {/* 재고 지표 5열 — 옛 규칙 실행이거나 stock 이 없으면 '—' 만 그린다. */}
                      <td className={ABC_COL_CLASS[9]} data-testid="abc-end-stock" style={{ ...td, textAlign: 'right' }}>{stockApplicable ? fmt(it.end_stock_qty) : '—'}</td>
                      <td className={ABC_COL_CLASS[10]} data-testid="abc-stock-days" style={{ ...td, textAlign: 'right' }}>{!stockApplicable ? '—' : (it.no_outbound ? '출고 없음' : fixed(it.stock_days, 1))}</td>
                      <td className={ABC_COL_CLASS[11]} data-testid="abc-turnover" style={{ ...td, textAlign: 'right' }}>{stockApplicable ? fixed(it.turnover, 2) : '—'}</td>
                      <td className={ABC_COL_CLASS[12]} data-testid="abc-idle-months" style={{ ...td, textAlign: 'right' }}>{!stockApplicable || it.idle_months === null || it.idle_months === undefined ? '—' : `${it.idle_months}${it.idle_truncated ? '+' : ''}`}</td>
                      <td className={ABC_COL_CLASS[13]} style={td}><span data-testid="abc-stock-status" data-status={stockApplicable ? (it.stock_status || '') : ''}
                        style={stockApplicable && it.stock_status ? badge(stockStatusTone[it.stock_status]) : undefined}>{stockApplicable ? (stockStatusName[it.stock_status] || '—') : '—'}</span></td>
                    </tr>)}
                    {!data?.items?.length && <tr><td colSpan={14} style={{ ...td, color: COLORS.textMuted }}>조회 결과가 없습니다.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '12px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                <button type="button" aria-label="첫 페이지" disabled={page <= 1 || busy} onClick={() => load({ targetPage: 1 })} style={btn('secondary', 'sm')}>« 처음</button>
                <button type="button" aria-label="이전 페이지" disabled={page <= 1 || busy} onClick={() => load({ targetPage: page - 1 })} style={btn('secondary', 'sm')}>이전</button>
                {pageWindow(page, totalPages).map((entry) => (typeof entry === 'number'
                  ? <button key={entry} type="button" aria-label={`${entry} 페이지`} aria-current={entry === page ? 'page' : undefined}
                    disabled={busy || entry === page} onClick={() => load({ targetPage: entry })}
                    style={btn(entry === page ? 'primary' : 'secondary', 'sm')}>{entry}</button>
                  : <span key={entry} aria-hidden="true" style={{ padding: '0 2px', color: COLORS.textMuted, fontSize: '12px' }}>…</span>))}
                <button type="button" aria-label="다음 페이지" disabled={page >= totalPages || busy} onClick={() => load({ targetPage: page + 1 })} style={btn('secondary', 'sm')}>다음</button>
                <button type="button" aria-label="마지막 페이지" disabled={page >= totalPages || busy} onClick={() => load({ targetPage: totalPages })} style={btn('secondary', 'sm')}>끝 »</button>
                <span data-testid="page-indicator" style={{ padding: '6px', fontSize: '12px' }}>{page} / {totalPages}</span>
                <input type="number" aria-label="이동할 페이지" min={1} max={totalPages} value={jumpPage} disabled={busy}
                  onChange={(e) => setJumpPage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goToPage(); } }}
                  style={{ ...input, width: '74px' }} />
                <button type="button" disabled={busy} onClick={goToPage} style={btn('secondary', 'sm')}>이동</button>
              </div>
            </section>
          </>}
        </Page>
      </main>
    </AppShell>
  );
}
